import { useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  Center,
  Flex,
  Image,
  Spinner,
  Text
} from '@chakra-ui/react'

import Modal from '../../modal'
import StyledInput from '../../styled/input'
import StyledTextarea from '../../styled/text-area'
import Stars from '../../verified-services/stars'
import { useRanContext } from '../../ran-state-provider'

import {
  getMyVerifiedServiceProfiles,
  updateMyVerifiedServiceProfile,
  updateMyVerifiedServiceAvailability,
  uploadImageToImgBB
} from '../../../lib/api-helper'

const DESCRIPTION_MAX = 500

const Field = ({ label, hint, children }) => (
  <Box w="full" mb={3}>
    <Text fontSize={11} fontWeight={700} letterSpacing={2} mb={1} align="left">
      {label}
    </Text>
    {children}
    {hint && (
      <Text fontSize={11} opacity={0.6} mt={1} align="left">
        {hint}
      </Text>
    )}
  </Box>
)

const VerifiedServiceProfile = ({ onClose, setToast }) => {
  const { setAuthentication } = useRanContext()

  const [isLoading, setLoading] = useState(true)
  const [isUploading, setUploading] = useState(false)
  const [isTogglingAvailability, setTogglingAvailability] = useState(false)
  const [profiles, setProfiles] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [inputVal, setInputVal] = useState(null)

  const fileInputRef = useRef(null)

  useEffect(() => {
    async function fetchData() {
      await fetchInitialData()
    }

    fetchData()
  }, [])

  const fetchInitialData = async () => {
    setLoading(true)

    const result = await getMyVerifiedServiceProfiles(setAuthentication)

    if (result?.success) {
      const data = result.data || []
      setProfiles(data)

      if (data.length) {
        setActiveId(data[0].id)
        setInputVal(toForm(data[0]))
      }
    }

    setLoading(false)
  }

  const toForm = profile => ({
    id: profile.id,
    displayName: profile.displayName || '',
    ingameName: profile.ingameName || '',
    description: profile.description || '',
    fee: profile.fee === null || profile.fee === undefined ? '' : String(Number(profile.fee)),
    contactLink: profile.contactLink || '',
    socialLink: profile.socialLink || '',
    avatarImage: profile.avatarImage || ''
  })

  const activeProfile = profiles.find(profile => profile.id === activeId)

  const onSelectProfile = profile => {
    setActiveId(profile.id)
    setInputVal(toForm(profile))
  }

  // Saves on click rather than waiting for SAVE - going live is a one tap action.
  const onToggleAvailability = async isAvailable => {
    if (isTogglingAvailability || !activeProfile) return
    if (activeProfile.isAvailable === isAvailable) return

    setTogglingAvailability(true)

    const result = await updateMyVerifiedServiceAvailability(
      { id: activeProfile.id, isAvailable },
      setAuthentication
    )

    if (result?.success) {
      setProfiles(current =>
        current.map(profile =>
          profile.id === result.data.id
            ? { ...profile, isAvailable: result.data.isAvailable }
            : profile
        )
      )
      setToast({
        status: 'success',
        messages: [`You are now shown as "${result.data.availabilityLabel}".`]
      })
    } else {
      setToast({
        status: 'error',
        messages: result?.errors?.length
          ? result.errors
          : ['Something went wrong']
      })
    }

    setTogglingAvailability(false)
  }

  const onPickPhoto = async event => {
    const file = event.target.files?.[0]
    if (!file) return

    setUploading(true)

    const url = await uploadImageToImgBB({ file })

    if (url) {
      setInputVal({ ...inputVal, avatarImage: url })
    } else {
      setToast({
        status: 'error',
        messages: ['Unable to upload the photo. Please try again.']
      })
    }

    setUploading(false)
    // Allow re-picking the same file after a failed upload.
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async () => {
    if (isLoading || isUploading || !inputVal) return
    setLoading(true)

    const fee = inputVal.fee === '' ? null : Number(inputVal.fee)

    if (fee !== null && Number.isNaN(fee)) {
      setToast({ status: 'error', messages: ['Fee must be a number.'] })
      setLoading(false)
      return
    }

    const result = await updateMyVerifiedServiceProfile(
      { ...inputVal, fee },
      setAuthentication
    )

    if (result?.success) {
      setToast({
        status: 'success',
        messages: ['Your verified service profile has been updated.']
      })
      onClose()
      return
    }

    setToast({
      status: 'error',
      messages: result?.errors?.length ? result.errors : ['Something went wrong']
    })
    setLoading(false)
  }

  return (
    <Modal
      header="VERIFIED SERVICE PROFILE"
      isOpen={true}
      isLoading={isLoading}
      onClose={() => onClose()}
      onSubmit={handleSubmit}
      submitText="SAVE"
      buttonLess={!profiles.length}
    >
      {isLoading ? (
        <Center>
          <Spinner />
        </Center>
      ) : !profiles.length ? (
        <Box align="center">
          <Text mb={2}>
            You are not listed as a verified middleman, booster, pilot or
            streamer.
          </Text>
          <Text fontSize={12} opacity={0.6}>
            Only the admin team can assign a verified role. Once assigned, you
            can fill in your own photo, fee, description and links here.
          </Text>
        </Box>
      ) : (
        <Box w="full">
          {profiles.length > 1 && (
            <Flex mb={4} flexWrap="wrap" justify="center">
              {profiles.map(profile => (
                <Button
                  key={`profile-${profile.id}`}
                  variant={
                    profile.id === activeId
                      ? 'maroon-mini'
                      : 'outline-mini-black'
                  }
                  onClick={() => onSelectProfile(profile)}
                  ml={1}
                  mb={1}
                >
                  {profile.typeName.toUpperCase()}
                </Button>
              ))}
            </Flex>
          )}

          {!inputVal?.displayName && (
            <Box bg="main-red" color="white" p={3} mb={4} fontSize={12}>
              You are not shown on the Verified Services page yet. Add a display
              name below and save - your account username is never published.
            </Box>
          )}

          <Box align="center" mb={4}>
            <Text fontSize={12} opacity={0.6}>
              Listed as <b>{activeProfile?.typeName}</b>
              {activeProfile?.enabled ? '' : ' (currently hidden by the admin)'}
            </Text>
            <Center mt={1}>
              <Stars
                averageScore={activeProfile?.averageScore}
                ratingCount={activeProfile?.ratingCount}
              />
            </Center>
          </Box>

          <Field
            label="AVAILABILITY"
            hint="Saved right away and shown on your card straight after."
          >
            <Flex>
              <Button
                variant={
                  activeProfile?.isAvailable
                    ? 'maroon-mini'
                    : 'outline-mini-black'
                }
                isDisabled={isTogglingAvailability}
                onClick={() => onToggleAvailability(true)}
                mr={2}
              >
                {(activeProfile?.availableLabel || 'Available').toUpperCase()}
              </Button>
              <Button
                variant={
                  activeProfile?.isAvailable
                    ? 'outline-mini-black'
                    : 'maroon-mini'
                }
                isDisabled={isTogglingAvailability}
                onClick={() => onToggleAvailability(false)}
              >
                {(
                  activeProfile?.unavailableLabel || 'Not available'
                ).toUpperCase()}
              </Button>
            </Flex>
          </Field>

          <Field label="PHOTO" hint="Shown on your card. Square images look best.">
            <Flex align="center">
              {inputVal?.avatarImage ? (
                <Image
                  src={inputVal.avatarImage}
                  alt="Your photo"
                  boxSize="46px"
                  borderRadius="full"
                  objectFit="cover"
                  mr={3}
                />
              ) : (
                <Flex
                  boxSize="46px"
                  borderRadius="full"
                  bg="light-gray"
                  align="center"
                  justify="center"
                  fontSize={11}
                  mr={3}
                >
                  None
                </Flex>
              )}

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={onPickPhoto}
                style={{ display: 'none' }}
              />

              <Button
                variant="outline-mini-black"
                isDisabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? 'UPLOADING...' : 'CHOOSE PHOTO'}
              </Button>

              {inputVal?.avatarImage && (
                <Button
                  variant="outline-mini-black"
                  ml={2}
                  onClick={() => setInputVal({ ...inputVal, avatarImage: '' })}
                >
                  REMOVE
                </Button>
              )}
            </Flex>
          </Field>

          <Field label="DISPLAY NAME" hint="The name shown on your card.">
            <StyledInput
              value={inputVal?.displayName}
              maxLength={50}
              onChange={e =>
                setInputVal({ ...inputVal, displayName: e.target.value })
              }
              placeholder="DISPLAY NAME"
            />
          </Field>

          <Field label="IN GAME NAME">
            <StyledInput
              value={inputVal?.ingameName}
              maxLength={50}
              onChange={e =>
                setInputVal({ ...inputVal, ingameName: e.target.value })
              }
              placeholder="IN GAME NAME"
            />
          </Field>

          <Field label="FEE" hint="Numbers only. Explain the details below.">
            <StyledInput
              value={inputVal?.fee}
              type="number"
              onChange={e => setInputVal({ ...inputVal, fee: e.target.value })}
              placeholder="FEE"
            />
          </Field>

          <Field label="DESCRIPTION">
            <StyledTextarea
              value={inputVal?.description}
              maxLength={DESCRIPTION_MAX}
              rows={4}
              onChange={e =>
                setInputVal({ ...inputVal, description: e.target.value })
              }
              placeholder="DESCRIPTION"
            />
            <Text fontSize={11} opacity={0.5} mt={1} align="right">
              {inputVal?.description?.length || 0}/{DESCRIPTION_MAX}
            </Text>
          </Field>

          <Field
            label="MESSAGE LINK"
            hint="Where players contact you. Must start with https://"
          >
            <StyledInput
              value={inputVal?.contactLink}
              maxLength={300}
              onChange={e =>
                setInputVal({ ...inputVal, contactLink: e.target.value })
              }
              placeholder="https://m.me/yourpage"
            />
          </Field>

          <Field
            label="SOCIAL LINK"
            hint="Your page or stream. Must start with https://"
          >
            <StyledInput
              value={inputVal?.socialLink}
              maxLength={300}
              onChange={e =>
                setInputVal({ ...inputVal, socialLink: e.target.value })
              }
              placeholder="https://facebook.com/yourpage"
            />
          </Field>
        </Box>
      )}
    </Modal>
  )
}

export default VerifiedServiceProfile
